import { parseHTMLToSlateJSON, parseSlateJSONToHTML } from "components/TextEditor/utils"
import LocationModal from "pages/AddProfile/LocationModal"
import { EditorValue } from "components/TextEditor/types"
import { useContext, useEffect, useState } from "react"
import { AlertContext } from "contexts/AlertContext"
import { useConfig } from "contexts/ConfigContext"
import InputDate from "components/inputs/Date"
import TextEditor from "components/TextEditor"
import { POST } from "api/request"
import Select from 'react-select'
import * as url from 'api/urls'
import Modal from "./Modal"

interface EditDataModalProps {
  onClose: () => void
  onReload: () => void
  data: board.BoardResponse | null | undefined
  shortVersion?: boolean
}

interface Option {
  value: string
  label: string
}

const EditDataModal: React.FC<EditDataModalProps> = ({ data, shortVersion = false, onClose, onReload }) => {
  const [biography, setBiography] = useState<EditorValue>(parseHTMLToSlateJSON(data?.data.biography || ''))
  const [selectedOption, setSelectedOption] = useState<Option | undefined>(undefined)
  const [sentence, setSentence] = useState<string>(data?.data.sentence || '')
  const [birth, setBirth] = useState<string>(data?.data.birth || '')
  const [death, setDeath] = useState<string>(data?.data.death || '')
  const [name, setName] = useState<string>(data?.data.name || '')
  const [isSending, setSending] = useState<boolean>(false)
  const [birthError, setBirthError] = useState<string>('')
  const [deathError, setDeathError] = useState<string>('')
  const [nameError, setNameError] = useState<string>('')
  const [modal, setModal] = useState<boolean>(false)
  const [gps, setGps] = useState<base.Coords | undefined>()
  const config = useConfig()

  const { showAlert } = useContext(AlertContext)

  useEffect(() => {
    const foundOption = config?.sentences.find(option => option.label === data?.data.sentence)
    if (foundOption) {
      setSelectedOption(foundOption)
      setSentence('')
    } else {
      setSelectedOption(config?.sentences.find(option => option.label === 'Inna'))
      setSentence(data?.data.sentence || '')
    }
  }, [data, config?.sentences])

  const parseDate = (dateString: string | undefined): Date | null => {
    if (!dateString) return null

    const [year, month, day] = dateString.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  const validateFields = () => {
    let errors = false

    if (!name.trim()) {
      setNameError('Imię i nazwisko są wymagane')
      errors = true
    } else {
      setNameError('')
    }

    const birthDate = parseDate(birth)
    const deathDate = parseDate(death)
    const today = new Date()

    if (!birth) {
      setBirthError('Data urodzenia jest wymagana')
      errors = true
    } else {
      setBirthError('')
    }
    if (!death) {
      setDeathError('Data śmierci jest wymagana')
      errors = true
    } else {
      setDeathError('')
    }
    if (deathDate && birthDate) {
      if (deathDate > today) {
        setDeathError('Data śmierci nie moze byc późniejsza niz dzisiaj')
        errors = true
        return !errors
      } else {
        setDeathError('')
      }
      if (birthDate > today) {
        setBirthError('Data urodzenia nie moze byc późniejsza niz dzisiaj')
        errors = true
        return !errors
      } else {
        setBirthError('')
      }
      if (deathDate < birthDate) {
        setBirthError('Data urodzin nie moze byc późniejsza niz data śmierci')
        setDeathError('Data śmierci nie moze byc wcześniejsza niz data urodzin')
        errors = true
        return !errors
      } else {
        setBirthError('')
        setDeathError('')
      }
    }
    return !errors
  }

  const handleSubmit = async () => {
    if (!validateFields()) {
      return
    }
    setSending(true)
    const formData = new FormData()

    if (selectedOption?.value === 'other') {
      formData.append('sentence', sentence)
    } else {
      formData.append('sentence', selectedOption?.label || 'Spoczywaj w spokoju')
    }

    formData.append('name', name)
    formData.append('birth', birth)
    formData.append('death', death)

    if (gps) {
      formData.append('gps[latitude]', gps.latitude.toString())
      formData.append('gps[longitude]', gps.longitude.toString())
    }

    if (biography) {
      formData.append('biography', parseSlateJSONToHTML(biography))
    }

    try {
      const response = await POST(url.updatedBoard(data?.data.id), {
        form: formData,
      })
      if (response.ok) {
        showAlert('Edytowano poprawnie', 'success')
        window.history.back()
        onReload()
        onClose()
      } else {
        showAlert('W trakcie wysyłania wystąpił błąd', 'error')
      }
    } catch (err) {
      console.error(err)
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      {modal
        ? <LocationModal onClose={() => setModal(false)} coords={gps} updatePosition={setGps} />
        : <Modal
          isLargeModal
          icon='settings'
          onClose={onClose}
          title={'Edytuj dane'}
          primaryDisabled={isSending}
          primaryButton={isSending ? "Wysyłanie..." : 'Wyślij zmiany'} primaryButtonAction={handleSubmit}
          secondaryButton={'Anuluj'} secondaryButtonAction={'close'}>

          <div className="modal__addPost">
            <div className="myInput">
              <label>{nameError ? nameError : 'Imię i nazwisko'}</label>
              <input
                className={nameError ? 'error' : ''}
                type="text"
                placeholder='Wpisz imię i nazwisko'
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  if (nameError) {
                    validateFields()
                  }
                }}
              />
            </div>

            {!shortVersion && <div className="myInput">
              <label>Wybierz sentencje</label>
              <Select
                classNamePrefix='mySelect'
                value={selectedOption}
                onChange={(newOption: any) => setSelectedOption(newOption)}
                options={config?.sentences}
              />
            </div>}

            {selectedOption?.value === 'other' &&
              <div className="myInput">
                <label>Własna sentencja</label>
                <input
                  type="text"
                  placeholder='Wpisz sentencje'
                  value={sentence}
                  onChange={(e) => setSentence(e.target.value)}
                />
              </div>
            }

            <InputDate
              value={birth}
              onChange={(date) => {
                setBirth(date);
                if (birthError) validateFields();
              }}
              label={birthError ? birthError : 'Data urodzenia'}
              error={birthError}
            />

            <InputDate
              value={death}
              onChange={(date) => {
                setDeath(date);
                if (deathError) validateFields();
              }}
              label={deathError ? deathError : 'Data śmierci'}
              error={deathError}
            />

            <div className="myInput" onClick={() => setModal(true)}>
              <label>Lokalizacja pomnika - kliknij, aby zmienić</label>
              <div className='empty'>{gps ? gps.latitude + ', ' + gps.longitude : data?.data.gps ? data?.data.gps.latitude + ', ' + data?.data.gps.longitude : 'Brak lokalizacji'}</div>
            </div>

            {!shortVersion && <div className="myInput">
              <label>Biografia</label>
              <TextEditor
                value={biography}
                setValue={(newBiography: EditorValue) => {
                  setBiography(newBiography)
                }}
              />
            </div>}
          </div>
        </Modal>}
    </>
  )
}

export default EditDataModal
